import styled from '@happymoneyinc/styled-components';
import { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import PropTypes from 'prop-types';
import ThemeWrapper from '../utils/ThemeWrapper/index.js';
import DotButtons from './DotButtons.js';
import ArrowButtons from './ArrowButtons.js';
import { jsx, jsxs } from 'react/jsx-runtime';

const Carousel = _ref => {
  let {
    children,
    className,
    css,
    customArrowButtons,
    customDotButtons,
    disabled,
    id,
    optionsUseEmblaCarousel,
    showArrowButtons,
    showDotButtons,
    testID
  } = _ref;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    ...optionsUseEmblaCarousel
  });
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
  const [prevButtonDisabled, setPrevButtonDisabled] = useState(true);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const onSelect = useCallback(() => {
    if (emblaApi) {
      setNextButtonDisabled(!(emblaApi !== null && emblaApi !== void 0 && emblaApi.canScrollNext()));
      setPrevButtonDisabled(!(emblaApi !== null && emblaApi !== void 0 && emblaApi.canScrollPrev()));
      setSelectedIndex(emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.selectedScrollSnap());
    }
  }, [emblaApi, setSelectedIndex]);
  const onScrollTo = useCallback(index => emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollTo(index), [emblaApi]);
  const onScrollToPrev = useCallback(() => {
    emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollPrev();
  }, [emblaApi]);
  const onScrollToNext = useCallback(() => {
    emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollNext();
  }, [emblaApi]);
  useEffect(() => {
    // Embla API is ready
    if (emblaApi) {
      emblaApi.on('select', onSelect);
      onSelect();
      setScrollSnaps(emblaApi.scrollSnapList());
    }
    return () => {
      emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.destroy();
      setNextButtonDisabled(true);
      setPrevButtonDisabled(true);
      setScrollSnaps([]);
      setSelectedIndex(0);
    };
  }, [emblaApi, setScrollSnaps, onSelect]);
  const getDots = () => {
    if (!showDotButtons) return null;
    const Dots = customDotButtons || DotButtons;
    return /*#__PURE__*/jsx(Dots, {
      selectedIndex: selectedIndex,
      onScrollTo: onScrollTo,
      scrollSnaps: scrollSnaps
    });
  };
  const getArrows = () => {
    if (!showArrowButtons) return null;
    const Arrows = customArrowButtons || ArrowButtons;
    return /*#__PURE__*/jsx(Arrows, {
      onScrollToNext: onScrollToNext,
      onScrollToPrev: onScrollToPrev,
      nextButtonDisabled: nextButtonDisabled,
      prevButtonDisabled: prevButtonDisabled
    });
  };
  return /*#__PURE__*/jsx(ThemeWrapper, {
    children: /*#__PURE__*/jsxs(_StyledEmblaCarousel, {
      "aria-disabled": disabled,
      className: `${className} embla`,
      "data-testid": testID,
      disabled: disabled,
      id: id,
      ref: emblaRef,
      $_css: css,
      children: [/*#__PURE__*/jsx(EmblaCarouselContainer, {
        className: "embla__container",
        children: children
      }), getArrows(), getDots()]
    })
  });
};
const EmblaCarousel = styled.div`
  position: relative;
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  overflow: hidden;

  &.is-draggable {
    cursor: grab;
  }

  &.is-dragging {
    cursor: grabbing;
  }
`;
var _StyledEmblaCarousel = styled(EmblaCarousel)`${p => p.$_css}`;
const EmblaCarouselContainer = styled.div`
  display: flex;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
`;
Carousel.propTypes = {
  /** data-test-id's to handle elements inside the Carousel component */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  className: PropTypes.string,
  css: PropTypes.string,
  /**
   * This property allows you to override the Arrows buttons component
   * customArrowButtons.propTypes = {
   *  onScrollToNext: PropTypes.func.isRequired,
   *  onScrollToPrev: PropTypes.func.isRequired,
   *  nextButtonDisabled: PropTypes.bool,
   *  nextButtonSvgIconType: PropTypes.string,
   *  prevButtonDisabled: PropTypes.bool,
   *  prevButtonSvgIconType: PropTypes.string
   * }
   * */
  customArrowButtons: PropTypes.elementType,
  /**
   * This property allows you to override the Dots component with buttons
   * Your customDotButtons component must receive the following properties
   * customDotButtons.PropTypes = {
   *  selectedIndex: PropTypes.number.isRequired,
   *  onScrollTo: PropTypes.func.isRequired, // onClick={() => onScrollTo(index)}
   *  scrollSnaps: PropTypes.array.isRequired,
   * }
   * */
  customDotButtons: PropTypes.elementType,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  showArrowButtons: PropTypes.bool,
  showDotButtons: PropTypes.bool,
  /**
   * For more info about options read documentation
   * https://www.embla-carousel.com/api/options/
   * and use the interface: EmblaOptionsType
   * happy-objects/node_modules/embla-carousel/react/index.d.ts
   * align: AlignmentOptionType; // 'start' | 'center' | 'end' | number;
   * axis: AxisOptionType;
   * containScroll: ScrollContainOptionType; // 'x' | 'y';
   * direction: DirectionOptionType; // 'ltr' | 'rtl';
   * dragFree: boolean;
   * draggable: boolean;
   * draggableClass: string;
   * draggingClass: string;
   * inViewThreshold: number;
   * loop: boolean;
   * selectedClass: string;
   * slidesToScroll: number;
   * speed: number;
   * startIndex: number;
   * */
  // eslint-disable-next-line react/forbid-prop-types
  optionsUseEmblaCarousel: PropTypes.objectOf(PropTypes.object),
  testID: PropTypes.string
};
Carousel.defaultProps = {
  className: '',
  css: undefined,
  customArrowButtons: null,
  customDotButtons: null,
  disabled: false,
  id: '',
  showArrowButtons: false,
  showDotButtons: true,
  optionsUseEmblaCarousel: {},
  testID: 'carousel'
};
var Carousel$1 = Carousel;

export { Carousel$1 as default };
//# sourceMappingURL=Carousel.js.map
